import { referenceDaf } from './DAFSinistres'
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Clock, FileText, MessageSquare, Bell, Euro,
  Calendar, MapPin, User, Shield, Building, AlertCircle,
  Download, Plus, ChevronDown, Car
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import SinistreTimeline from '../../../components/shared/SinistreTimeline'
import SinistreDocuments from '../../../components/shared/SinistreDocuments'

const API_URL = process.env.REACT_APP_BACKEND_URL

const STATUT_CONFIG = {
  declare:               { label: 'Déclaré',              color: 'bg-slate-100 text-slate-700',   dot: 'bg-slate-400' },
  en_instruction:        { label: 'En instruction',       color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  en_attente_compagnie:  { label: 'Attente compagnie',    color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  expertise_en_cours:    { label: 'Expertise en cours',   color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  negociation:           { label: 'Négociation',          color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  cloture_favorable:     { label: 'Clôturé favorablement',color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cloture_refus:         { label: 'Clôturé - Refus',      color: 'bg-red-100 text-red-700',       dot: 'bg-red-500' },
  sans_suite:            { label: 'Sans suite',           color: 'bg-slate-100 text-slate-500',   dot: 'bg-slate-300' },
}

const TYPE_SINISTRE_LABELS = {
  dab: 'Dommages aux biens',
  flotte: 'Flotte automobile',
  rc_tiers: 'Responsabilité civile',
  amiable: 'Règlement amiable',
  contentieux: 'Contentieux',
  autre: 'Autre',
}

export default function DAFSinistreDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [sinistre, setSinistre] = useState(null)
  const [events, setEvents] = useState([])
  const [documents, setDocuments] = useState([])
  const [provision, setProvision] = useState(null)
  const [relances, setRelances] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('timeline')

  useEffect(() => {
    if (id) fetchSinistre()
  }, [id])

  async function fetchSinistre() {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres/${id}/detail`)
      const data = await res.json()
      if (!data.success) throw new Error(data.detail || 'Erreur')
      setSinistre(data.sinistre)
      // Filter out internal notes for DAF view
      setEvents((data.events || []).filter(e => e.type_evenement !== 'note_interne'))
      // Filter out confidential documents for DAF view
      setDocuments((data.documents || []).filter(d => !d.est_confidentiel))
      setRelances((data.relances || []).filter(r =>
        ['client', 'declarant'].includes(r.destinataire_type)
      ).slice(0, 5))
      // Provisions are included in sinistre data
      setProvision(data.sinistre?.provision_compagnie ? {
        montant_actuel: data.sinistre.provision_compagnie,
        source: data.sinistre.provision_source
      } : null)
    } catch (error) {
      console.error('Error fetching sinistre:', error)
      toast.error('Erreur lors du chargement du dossier')
    } finally {
      setLoading(false)
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  function formatCurrency(amount) {
    if (amount == null) return '—'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Chargement du dossier...</p>
        </div>
      </div>
    )
  }

  if (!sinistre) {
    return (
      <div className="text-center py-20">
        <AlertCircle size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">Dossier introuvable</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-sm text-emerald-600 hover:underline"
        >
          Retour
        </button>
      </div>
    )
  }

  const statutConfig = STATUT_CONFIG[sinistre.statut] || STATUT_CONFIG.declare

  return (
    <div className="p-8 pb-12" data-testid="daf-sinistre-detail">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mt-1 w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors shrink-0"
        >
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="daf-detail-reference">
              {referenceDaf(sinistre)}
            </h1>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statutConfig.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statutConfig.dot}`} />
              {statutConfig.label}
            </span>
            {sinistre.est_amiable && (
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                Amiable
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {sinistre.nature_sinistre || TYPE_SINISTRE_LABELS[sinistre.type_sinistre]}
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-semibold text-slate-800 text-sm">Informations générales</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <InfoRow 
                  icon={<Calendar size={14} />}
                  label="Date du sinistre"
                  value={formatDate(sinistre.date_fait)}
                />
                <InfoRow 
                  icon={<Calendar size={14} />}
                  label="Date de déclaration"
                  value={formatDate(sinistre.date_declaration)}
                />
                <InfoRow 
                  icon={<FileText size={14} />}
                  label="Type"
                  value={TYPE_SINISTRE_LABELS[sinistre.type_sinistre]}
                />
                {sinistre.lieu_sinistre?.adresse && (
                  <InfoRow 
                    icon={<MapPin size={14} />}
                    label="Lieu"
                    value={sinistre.lieu_sinistre.adresse}
                    colSpan
                  />
                )}
                {sinistre.vehicules && (
                  <InfoRow 
                    icon={<Car size={14} className="text-blue-500" />}
                    label="Véhicule"
                    value={`${sinistre.vehicules.immatriculation}${sinistre.vehicules.marque ? ` — ${sinistre.vehicules.marque} ${sinistre.vehicules.modele || ''}` : ''}`}
                  />
                )}
                {sinistre.sites && (
                  <InfoRow 
                    icon={<MapPin size={14} className="text-emerald-500" />}
                    label="Site"
                    value={`${sinistre.sites.nom}${sinistre.sites.ville ? ` — ${sinistre.sites.ville}` : ''}`}
                  />
                )}
              </div>

              {sinistre.description && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-2">Description</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {sinistre.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Police card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-semibold text-slate-800 text-sm">Police d'assurance</h2>
            </div>
            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Shield size={18} className="text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">
                    {sinistre.polices?.libelle || sinistre.polices?.type_police}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    N° {sinistre.polices?.numero_police}
                  </p>
                  {sinistre.polices?.compagnies?.nom && (
                    <p className="text-xs text-slate-400 mt-1">
                      <Building size={12} className="inline mr-1" />
                      {sinistre.polices.compagnies.nom}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-200">
              {[
                { key: 'timeline', label: 'Historique', icon: Clock, count: events.length },
                { key: 'documents', label: 'Documents', icon: FileText, count: documents.length },
                { key: 'relances', label: 'Relances', icon: Bell, count: relances.length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`daf-tab-${tab.key}`}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2
                    ${activeTab === tab.key 
                      ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50' 
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  <tab.icon size={15} />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.key ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-5">
              {activeTab === 'timeline' && (
                <SinistreTimeline events={events} />
              )}
              {activeTab === 'documents' && (
                <SinistreDocuments 
                  documents={documents} 
                  sinistreId={id}
                  tenantId={sinistre.tenant_id}
                  onRefresh={fetchSinistre}
                  canUpload={false}
                  userRole={profile?.role || 'daf'}
                />
              )}
              {activeTab === 'relances' && (
                <RelancesList relances={relances} />
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Financial summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-semibold text-slate-800 text-sm">Évaluation financière</h2>
            </div>
            <div className="p-5 space-y-4">
              <FinancialRow 
                label="Dommages estimés"
                value={formatCurrency(sinistre.montant_dommages_estime)}
                primary
              />
              {provision && (
                <FinancialRow 
                  label="Provision actuelle"
                  value={formatCurrency(provision.montant)}
                  highlight
                />
              )}
              {sinistre.montant_reclamation && (
                <FinancialRow 
                  label="Réclamation"
                  value={formatCurrency(sinistre.montant_reclamation)}
                />
              )}
              {sinistre.montant_offre_compagnie && (
                <FinancialRow 
                  label="Offre compagnie"
                  value={formatCurrency(sinistre.montant_offre_compagnie)}
                />
              )}
              {sinistre.montant_rembourse && (
                <FinancialRow 
                  label="Remboursé"
                  value={formatCurrency(sinistre.montant_rembourse)}
                  success
                />
              )}
              {sinistre.economie_amiable && (
                <FinancialRow 
                  label="Économie amiable"
                  value={formatCurrency(sinistre.economie_amiable)}
                  success
                />
              )}
            </div>
          </div>

          {/* Declarant */}
          {sinistre.declarant && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
                <h2 className="font-semibold text-slate-800 text-sm">Déclarant</h2>
              </div>
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500">
                    {sinistre.declarant.prenom[0]}{sinistre.declarant.nom[0]}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      {sinistre.declarant.prenom} {sinistre.declarant.nom}
                    </p>
                    <p className="text-sm text-slate-500">{sinistre.declarant.email}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick stats */}
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-xl p-5 text-white">
            <p className="text-xs font-medium text-emerald-300 mb-3">Résumé du dossier</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-200">Événements</span>
                <span className="font-mono font-bold">{events.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-200">Documents</span>
                <span className="font-mono font-bold">{documents.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-200">Ancienneté</span>
                <span className="font-mono font-bold">
                  {Math.floor((new Date() - new Date(sinistre.date_declaration)) / (1000 * 60 * 60 * 24))}j
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value, colSpan }) {
  return (
    <div className={colSpan ? 'col-span-2' : ''}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        {icon}
        {label}
      </div>
      <p className="text-sm text-slate-800 font-medium">{value}</p>
    </div>
  )
}

function FinancialRow({ label, value, primary, highlight, success }) {
  return (
    <div className={`flex justify-between items-center ${primary ? 'pb-3 border-b border-slate-100' : ''}`}>
      <span className={`text-sm ${primary ? 'font-medium text-slate-700' : 'text-slate-500'}`}>
        {label}
      </span>
      <span className={`font-mono text-sm font-semibold ${
        success ? 'text-emerald-600' : 
        highlight ? 'text-amber-600' : 
        primary ? 'text-slate-900' : 'text-slate-700'
      }`}>
        {value}
      </span>
    </div>
  )
}

function RelancesList({ relances }) {
  if (!relances || relances.length === 0) {
    return (
      <div className="text-center py-8">
        <Bell size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-400">Aucune relance envoyée</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {relances.map(r => (
        <div 
          key={r.id}
          className="p-3 bg-slate-50 rounded-lg border border-slate-200"
        >
          <div className="flex justify-between items-start mb-1">
            <p className="text-sm font-medium text-slate-800">{r.sujet}</p>
            <span className={`text-xs px-2 py-0.5 rounded ${
              r.statut === 'repondue' ? 'bg-green-100 text-green-700' :
              r.statut === 'envoyee' ? 'bg-blue-100 text-blue-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {r.statut === 'repondue' ? 'Répondu' : r.statut === 'envoyee' ? 'Envoyé' : r.statut}
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Envoyé le {new Date(r.date_envoi || r.created_at).toLocaleDateString('fr-FR')}
          </p>
        </div>
      ))}
    </div>
  )
}
